import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const SignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    department: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e?.target?.name]: e?.target?.value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (formData?.password !== formData?.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData?.password?.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await authService?.signUp(
        formData?.email,
        formData?.password,
        formData?.fullName,
        formData?.department
      );

      if (error) {
        setError(error?.message || 'Registration failed. Please try again.');
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-background rounded-2xl shadow-xl border border-border p-8 text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={32} className="text-success" />
            </div>
            
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Registration Successful!
            </h2>
            
            <p className="text-muted-foreground mb-6">
              Please check your email for a confirmation link to activate your account.
            </p>
            
            <Button
              variant="default"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Continue to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-background rounded-2xl shadow-xl border border-border p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
              <Icon name="UserPlus" size={24} className="text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Create Account
            </h1>
            <p className="text-muted-foreground">
              Join ZentriDMS to manage your documents
            </p>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <Icon name="AlertTriangle" size={16} />
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <Input
                label="Full Name"
                name="fullName"
                value={formData?.fullName}
                onChange={handleChange}
                required
                placeholder="Enter your full name"
                disabled={loading}
              />

              <Input
                label="Email Address"
                name="email"
                type="email"
                value={formData?.email}
                onChange={handleChange}
                required
                placeholder="Enter your email"
                disabled={loading}
              />

              <Input
                label="Department (Optional)"
                name="department"
                value={formData?.department}
                onChange={handleChange}
                placeholder="e.g., IT, Marketing, Operations"
                disabled={loading}
              />

              <Input
                label="Password"
                name="password"
                type="password"
                value={formData?.password}
                onChange={handleChange}
                required
                placeholder="Create a password (min. 6 characters)"
                disabled={loading}
              />

              <Input
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData?.confirmPassword}
                onChange={handleChange}
                required
                placeholder="Confirm your password"
                disabled={loading}
              />
            </div>

            <Button
              type="submit"
              variant="default"
              loading={loading}
              className="w-full"
              iconName={loading ? "Loader" : "UserPlus"}
              iconPosition="left"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-primary hover:text-primary/80 font-medium transition-colors"
              >
                Sign In
              </button>
            </p>
          </div>

          {/* Email confirmation notice */}
          <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Email Confirmation Required</p>
                <p>After registration, check your email for a confirmation link to activate your account.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;